'use client'

import { useEffect } from 'react'
import { useSubBrand } from '@/providers/SubBrand'
import type { SubBrand } from '@/providers/SubBrand/types'

/**
 * SubBrandSync - Synchronizes sub-brand state with React context from server-side data
 * 
 * @description A utility component that bridges server-side sub-brand data with client-side
 * React context state management. This component ensures that the sub-brand context is
 * properly initialized and synchronized when rendering pages with specific sub-brand
 * configurations. The component applies the sub-brand to the document element via the
 * data-sub-brand attribute, enabling CSS theming and visual differentiation between
 * Civo's main brand and AI sub-brand experiences.
 * 
 * @example
 * // Server-side page component with Civo main brand
 * export default function CivoPage() {
 *   return (
 *     <>
 *       <SubBrandSync subBrand="civo" />
 *       <Header />
 *       <MainContent />
 *       <Footer />
 *     </>
 *   )
 * }
 * 
 * @example
 * // Server-side page component with AI sub-brand
 * export default function AIPage() {
 *   return (
 *     <>
 *       <SubBrandSync subBrand="ai" />
 *       <AIHeader />
 *       <AIContent />
 *       <AIFooter />
 *     </>
 *   )
 * }
 * 
 * @example
 * // Dynamic sub-brand based on page data
 * export default function DynamicPage({ pageData }: { pageData: { subBrand: SubBrand } }) {
 *   return (
 *     <>
 *       <SubBrandSync subBrand={pageData.subBrand} />
 *       <PageContent />
 *     </>
 *   )
 * }
 * 
 * @example
 * // Using in layout files for consistent branding
 * export default function Layout({ 
 *   children, 
 *   params 
 * }: { 
 *   children: React.ReactNode
 *   params: { subBrand: SubBrand }
 * }) {
 *   return (
 *     <html>
 *       <body>
 *         <SubBrandProvider>
 *           <SubBrandSync subBrand={params.subBrand} />
 *           {children}
 *         </SubBrandProvider>
 *       </body>
 *     </html>
 *   )
 * }
 * 
 * @example
 * // Integration with Next.js page props
 * export async function getServerSideProps(context) {
 *   const subBrand = determineSubBrandFromPath(context.resolvedUrl)
 *   return { props: { subBrand } }
 * }
 * 
 * export default function Page({ subBrand }: { subBrand: SubBrand }) {
 *   return (
 *     <>
 *       <SubBrandSync subBrand={subBrand} />
 *       <PageContent />
 *     </>
 *   )
 * }
 */

/**
 * SubBrandSyncProps interface
 * 
 * @description Props for the SubBrandSync component that specify which sub-brand
 * configuration should be applied to the application context.
 * 
 * @property {SubBrand} subBrand - The sub-brand identifier ('civo' | 'ai') that determines
 * the visual theme and branding context for the current page or section
 */
interface SubBrandSyncProps {
  subBrand: SubBrand
}

export const SubBrandSync: React.FC<SubBrandSyncProps> = ({ subBrand }) => {
  const { setSubBrand } = useSubBrand()

  useEffect(() => {
    if (setSubBrand) {
      setSubBrand(subBrand)
    }
  }, [subBrand, setSubBrand])

  return null
}